import { Collateral, CollateralCategoryOptions, File } from 'models';
import { FileHelpers } from 'helpers/FileHelpers';
import { ImageKitHelpers } from 'helpers/ImageKitHelpers';
import { IntlHelpers } from 'helpers/IntlHelpers';
import { VideoHelpers } from 'helpers/VideoHelpers';

enum CollateralGroup {
    DOCUMENTS = 'documents',
    VIDEOS = 'videos',
    IMAGES = 'images',
}

const CollateralHelpers = {
    swr: {
        list: (companyId: string) => `/api/collateral/company/${companyId}`,
        item: (collateralId: string) => `/api/collateral/${collateralId}`,
        byProduct: (productId: string) => `/api/collateral/product/${productId}`,
        productFiles: (companyId: string) => `/api/collateral/product-files/${companyId}`,
    },

    hasPreview: (file: Pick<File, 'mimeType'>) =>
        FileHelpers.isPdf(file) || FileHelpers.isImage(file) || FileHelpers.isVideo(file),

    file: (collateral: Pick<Collateral, 'file'>) =>
        collateral.file && typeof collateral.file === 'object' ? collateral.file : null,

    videoLink: (collateral: Pick<Collateral, 'videoUrl'>) =>
        collateral.videoUrl ? VideoHelpers.parseLink(collateral.videoUrl) : null,

    isViewable: (collateral: Pick<Collateral, 'file' | 'videoUrl'>) =>
        Boolean(CollateralHelpers.file(collateral) || CollateralHelpers.videoLink(collateral)),

    isPlayable: (collateral: Pick<Collateral, 'file' | 'videoUrl'>) => {
        const file = CollateralHelpers.file(collateral);

        return Boolean(CollateralHelpers.videoLink(collateral) || (file && FileHelpers.isVideo(file)));
    },

    group: (collateral: Pick<Collateral, 'file' | 'videoUrl'>) => {
        const file = CollateralHelpers.file(collateral);

        if (CollateralHelpers.isPlayable(collateral)) {
            return CollateralGroup.VIDEOS;
        }

        return file && FileHelpers.isImage(file) ? CollateralGroup.IMAGES : CollateralGroup.DOCUMENTS;
    },

    categoryLabel: (collateral: Pick<Collateral, 'category'>) =>
        CollateralCategoryOptions.find(({ value }) => value === collateral.category)?.label,

    caption: (collateral: Pick<Collateral, 'category' | 'file' | 'videoUrl'>) =>
        [CollateralHelpers.categoryLabel(collateral), CollateralHelpers.kind(collateral)].filter(Boolean).join(' · '),

    kind: (collateral: Pick<Collateral, 'file' | 'videoUrl'>) => {
        const link = CollateralHelpers.videoLink(collateral);
        const file = CollateralHelpers.file(collateral);

        if (link) {
            return VideoHelpers.providerName(link.provider);
        }

        if (!file) {
            return 'File';
        }

        if (FileHelpers.isPdf(file)) {
            const pageCount = CollateralHelpers.pageCount(file);

            return pageCount ? IntlHelpers.formatCount(pageCount, 'page', 'pages') : 'PDF';
        }

        if (FileHelpers.isImage(file)) {
            return 'Image';
        }

        return FileHelpers.isVideo(file) ? 'Video' : 'File';
    },

    // PDFs uploaded before page counting existed (often reused product files) have no count yet
    pageCount: (file: Pick<File, 'mimeType' | 'pageCount'>) => (FileHelpers.isPdf(file) ? file.pageCount || null : 1),

    pageSrc: (file: File, page: number, width: number) =>
        FileHelpers.isPdf(file)
            ? (ImageKitHelpers.getDocumentThumbnailSrc(file, { page, width }) ?? file.url)
            : FileHelpers.isVideo(file)
              ? (ImageKitHelpers.getDocumentThumbnailSrc(file, { width }) ?? file.url)
              : ImageKitHelpers.getSrcFromFile(file, { width, height: undefined }),

    titleFromFilename: (filename: string) =>
        filename
            .replace(/\.[^.]+$/, '')
            .replace(/[-_]+/g, ' ')
            .trim(),
};

export { CollateralGroup, CollateralHelpers };
